import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Mail, Shield, HelpCircle, Download, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardData } from '@/hooks/useDashboardData';
import { Button } from '@/components/ui/button';

import { ProfileHeaderCard } from '@/components/profile/ProfileHeaderCard';
import { RemindersSection, ReminderConfig } from '@/components/profile/RemindersSection';
import { ProfileCommunityCard } from '@/components/profile/ProfileCommunityCard';
import { SettingsGroup } from '@/components/profile/SettingsGroup';
import { EditProfileDialog } from '@/components/profile/EditProfileDialog';
import { useToast } from '@/hooks/use-toast';

const Profile = () => {
  const { user, signOut } = useAuth();
  const data = useDashboardData();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [localName, setLocalName] = useState<string | null>(null);
  const [localAvatar, setLocalAvatar] = useState<string | null>(null);
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const userName = localName || data.userName;
  const avatar = localAvatar || data.avatarUrl;

  const [reminders, setReminders] = useState<ReminderConfig[]>([
    { id: 'checkin', label: 'Check-in diario', active: true, time: '20:00', frequency: 'Todos los días' },
    { id: 'hydration', label: 'Hidratación', active: true, frequency: 'Cada 3 horas' },
    { id: 'protocol', label: 'Protocolo del día', active: false },
  ]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Check if app can be installed
    if ((window as any).deferredPrompt) {
      setCanInstall(true);
    }

    const handleAppInstalled = () => {
      setCanInstall(false);
      (window as any).deferredPrompt = null;
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  const handleInstallApp = async () => {
    const promptEvent = (window as any).deferredPrompt;
    if (!promptEvent) return;

    promptEvent.prompt();
    (window as any).deferredPrompt = null;
    setCanInstall(false);
  };

  const handleSaveName = async (newName: string) => {
    if (!user) return;
    
    try {
      const res = await fetch('/api/profile/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payload: { 
            full_name: newName
          }
        })
      });
        
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to update name");
      
      setLocalName(newName);
      toast({ title: "Perfil actualizado", description: "Tu nombre se ha guardado correctamente." });
    } catch (error: any) {
      console.error("Error updating name:", error);
      toast({ title: "Error", description: error.message || "No se pudo actualizar el nombre.", variant: "destructive" });
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result?.toString().split(',')[1];
        resolve(base64String || '');
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && user) {
      setLoadingAvatar(true);
      
      try {
        const base64Data = await fileToBase64(file);
        
        const res = await fetch('/api/profile/avatar-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type,
            base64Data
          })
        });
            
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || "Failed to upload photo");
          
        setLocalAvatar(result.avatarUrl);
        toast({ title: "Foto actualizada", description: "Tu foto de perfil se ha guardado correctamente." });
      } catch (error: any) {
        console.error("Error updating avatar:", error);
        toast({ title: "Error", description: error.message || "No se pudo actualizar la foto.", variant: "destructive" });
      } finally {
        setLoadingAvatar(false);
        setIsEditDialogOpen(false);
      }
    }
  };

  const toggleReminder = (id: string, active: boolean) => {
    setReminders(prev => prev.map(r => r.id === id ? { ...r, active } : r));
  };

  const handleEditProfile = () => {
    setIsEditDialogOpen(true);
  };

  const handleNotImplemented = () => {
    toast({ title: "Próximamente", description: "Esta función estará disponible en futuras actualizaciones." });
  };

  const triggerAvatarUpload = () => {
    if (!loadingAvatar && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };



  return (
    <div className="p-4 md:p-8 max-w-md mx-auto space-y-6 pb-28 animate-fade-in">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-heading font-bold text-foreground">Mi Cuenta</h1>
      </div>

      {/* Hidden file input for avatar upload triggered from either the dialog or the header card */}
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleAvatarChange} 
        accept="image/*" 
        className="hidden" 
      />

      {/* Section 1: Main Header */}
      <ProfileHeaderCard
        userName={userName}
        email={user?.email || 'usuario@ejemplo.com'}
        avatar={avatar}
        loadingAvatar={loadingAvatar}
        mainGoal={data.mainGoal}
        protocolStatus={`Día ${data.protocol.totalCompleted + 1} de 21`}
        onAvatarChange={handleAvatarChange}
        onEditProfile={handleEditProfile}
      />

      {/* Section: Descarga do Guia Reset */}
      <div className="bg-card rounded-2xl p-5 shadow-card space-y-4">
        <div className="flex items-center gap-3 text-left">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <p className="font-bold text-sm">Método Reset 3 Días</p>
            <p className="text-xs text-muted-foreground">Tu guía inicial en PDF</p>
          </div>
        </div>
        
        <Button asChild variant="outline" className="w-full h-10 rounded-xl border-primary/20 hover:bg-primary/5">
          <a href="/Metodo-Reset-3-Dias.pdf" target="_blank" rel="noopener noreferrer">
            <Download className="h-4 w-4 mr-2 text-primary" /> Descargar Guía
          </a>
        </Button>
      </div>

      {/* Section 4: Reminders */}
      <RemindersSection
        reminders={reminders}
        onToggle={toggleReminder}
        onEdit={handleNotImplemented}
      />

      {/* Section 5: Community */}
      <ProfileCommunityCard newConversationsCount={2} />

      {/* Section 6: Settings & Account */}
      <div className="space-y-6">
        <SettingsGroup
          title="Cuenta"
          items={[
            { id: 'edit-profile', label: 'Editar foto y nombre', icon: User, onClick: handleEditProfile },
            { id: 'email', label: 'Correo electrónico', icon: Mail, value: user?.email || '', readOnly: true, helperText: 'El correo no se puede cambiar' },
          ]}
        />

        <SettingsGroup
          title="Aplicación"
          items={[
            ...(canInstall ? [{ id: 'install', label: 'Instalar App', icon: Download, onClick: handleInstallApp }] : []),
            { id: 'privacy', label: 'Privacidad', icon: Shield, onClick: handleNotImplemented },
            { id: 'support', label: 'Soporte y Ayuda', icon: HelpCircle, onClick: handleNotImplemented },
          ]}
        />
      </div>

      {/* Disclaimer */}
      <div className="bg-muted/50 rounded-2xl p-4 border border-border">
        <p className="text-[11px] text-muted-foreground leading-relaxed text-center">
          ⚕️ Esta aplicación está destinada al bienestar, educación y apoyo a la creación de hábitos. 
          No sustituye la evaluación médica o nutricional.
        </p>
      </div>

      {/* Section 7: Logout */}
      <Button 
        variant="ghost" 
        className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive h-12 rounded-xl" 
        onClick={handleLogout}
      >
        <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
      </Button>

      {/* Edit Profile Dialog */}
      <EditProfileDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        currentName={userName}
        onSave={handleSaveName}
        onAvatarClick={triggerAvatarUpload}
      />
    </div>
  );
};

export default Profile;